import { useLayoutEffect, useState } from "react";

/** Pixel sizes of the grid's own scrollbars; 0 while an axis doesn't overflow or the scrollbars overlay. */
export interface SpreadsheetScrollbarSizes {
    horizontalHeight: number;
    verticalWidth: number;
}

// Sub-pixel layout still counts as scrolled to the end.
const EDGE_TOLERANCE_PX = 1;

const NO_SCROLLBARS: SpreadsheetScrollbarSizes = {
    horizontalHeight: 0,
    verticalWidth: 0,
};

function updateOverflowEdges(grid: HTMLElement): void {
    const maxScrollLeft = grid.scrollWidth - grid.clientWidth;
    grid.toggleAttribute("data-overflow-top", grid.scrollTop > 0);
    grid.toggleAttribute("data-overflow-start", grid.scrollLeft > 0);
    grid.toggleAttribute(
        "data-overflow-end",
        grid.scrollLeft < maxScrollLeft - EDGE_TOLERANCE_PX,
    );
}

function measureScrollbars(grid: HTMLElement): SpreadsheetScrollbarSizes {
    return {
        horizontalHeight: grid.offsetHeight - grid.clientHeight,
        verticalWidth: grid.offsetWidth - grid.clientWidth,
    };
}

/**
 * Marks which edges have content scrolled under them, as `data-overflow-top`
 * (rows under the sticky header), `data-overflow-start` and
 * `data-overflow-end` on the grid root (so pinned columns draw their edge
 * line only then), and returns the grid's scrollbar
 * sizes so a bar floating over the grid can keep clear of them. The
 * attributes are written on scroll without re-rendering. Pass a `null` grid
 * to skip tracking; a new grid element (e.g. after a remount) is tracked
 * instead of the old one. `columnLayout` is the grid's column track list:
 * columns added or removed change the scroll width without resizing the
 * grid, so the edges are re-checked whenever it changes.
 */
export function useSpreadsheetScrollEdges({
    grid,
    columnLayout,
}: {
    grid: HTMLElement | null;
    columnLayout: string;
}): SpreadsheetScrollbarSizes {
    const [scrollbarSizes, setScrollbarSizes] = useState(NO_SCROLLBARS);

    useLayoutEffect(() => {
        if (!grid) {
            return undefined;
        }
        const handleScroll = () => updateOverflowEdges(grid);
        // A scrollbar showing or hiding resizes the grid's content box, so
        // this also catches overflow that starts or ends.
        const resizeObserver = new ResizeObserver(() => {
            updateOverflowEdges(grid);
            const next = measureScrollbars(grid);
            setScrollbarSizes((previous) =>
                previous.horizontalHeight === next.horizontalHeight &&
                previous.verticalWidth === next.verticalWidth
                    ? previous
                    : next,
            );
        });
        grid.addEventListener("scroll", handleScroll, { passive: true });
        resizeObserver.observe(grid);
        return () => {
            grid.removeEventListener("scroll", handleScroll);
            resizeObserver.disconnect();
        };
    }, [grid]);

    useLayoutEffect(() => {
        if (grid) {
            updateOverflowEdges(grid);
        }
    }, [grid, columnLayout]);

    return scrollbarSizes;
}
